// Quick-add option picker (snippets/pt-variant-picker.liquid).
//
// Keeps a quick-add form pointed at the variant the shopper picked, and shows
// which variants of the same product are already in the cart so a second
// color/size never lands as a surprise duplicate line. "Swap" removes the
// other variant(s) and adds the selected one.

(() => {
  if (customElements.get('pt-variant-picker')) return;

  // One shared /cart.js read for every picker on the page.
  const ptCart = (() => {
    let items = null;
    let inflight = null;
    let subscribed = false;
    const watchers = new Set();

    const notify = () => watchers.forEach((fn) => fn(items));

    const refresh = () => {
      if (!inflight) {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        inflight = fetch(`${cartUrl}.js`)
          .then((response) => response.json())
          .then((cart) => {
            items = Array.isArray(cart.items) ? cart.items : [];
            notify();
          })
          .catch((error) => console.error(error))
          .finally(() => {
            inflight = null;
          });
      }
      return inflight;
    };

    const listen = () => {
      if (subscribed || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      subscribed = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Only a full cart (has item_count) is safe to use directly; /cart/add
        // responses list just the added lines.
        const cart = event && event.cartData;
        if (cart && Array.isArray(cart.items) && 'item_count' in cart) {
          items = cart.items;
          notify();
          return;
        }
        refresh();
      });
    };

    return {
      watch(fn) {
        watchers.add(fn);
        listen();
        if (items) fn(items);
        else refresh();
      },
      unwatch(fn) {
        watchers.delete(fn);
      },
      refresh,
    };
  })();

  const LABEL_SELECTOR =
    '[data-pt-button-label], .pt-cart-addon-action, .pt-cart-gift-price, .cart-notification__addon-action';

  class PtVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      const data = this.querySelector('[data-pt-variants]');
      this.form = (this.dataset.form && document.getElementById(this.dataset.form)) || this.closest('form');
      if (!data || !this.form) return;

      this.ready = true;
      this.variants = JSON.parse(data.textContent);
      const imageData = this.querySelector('[data-pt-images]');
      this.images = imageData ? JSON.parse(imageData.textContent) : null;
      this.productId = Number(this.dataset.productId);
      this.input = this.form.querySelector('[name="id"]');
      this.button = this.form.querySelector('[type="submit"]');
      this.label = this.button && this.button.querySelector(LABEL_SELECTOR);
      this.card = this.closest('[data-pt-quick-add]') || this.form.closest('[data-pt-quick-add]');
      this.image = (this.card || this.form).querySelector('[data-pt-variant-image]');
      this.selects = Array.from(this.querySelectorAll('select[data-pt-option-index]'));
      this.inCart = this.querySelector('[data-pt-in-cart]');
      this.inCartText = this.querySelector('[data-pt-in-cart-text]');
      this.swapButton = this.querySelector('[data-pt-swap]');
      this.cartItems = [];

      this.selects.forEach((select) => select.addEventListener('change', () => this.update(select)));
      this.swapButton.addEventListener('click', () => this.swap());
      this.onCart = (items) => {
        this.cartItems = items || [];
        this.render();
      };

      this.update();
      ptCart.watch(this.onCart);
    }

    disconnectedCallback() {
      if (this.onCart) ptCart.unwatch(this.onCart);
      this.ready = false;
    }

    get selectedVariant() {
      const picked = this.variants[0] ? this.variants[0].options.slice() : [];
      this.selects.forEach((select) => {
        picked[Number(select.dataset.ptOptionIndex)] = select.value;
      });
      return this.variants.find((variant) => variant.options.every((value, i) => value === picked[i])) || null;
    }

    // Apparel has sparse option grids (not every style comes in every color),
    // so a pick that makes a non-existent combination moves the other selects
    // to the closest in-stock variant that keeps the value just picked.
    snapTo(changed) {
      const index = Number(changed.dataset.ptOptionIndex);
      const picked = this.selects.map((select) => select.value);
      const score = (variant) =>
        this.selects.reduce((sum, select, i) => {
          const optionIndex = Number(select.dataset.ptOptionIndex);
          return sum + (optionIndex !== index && variant.options[optionIndex] === picked[i] ? 2 : 0);
        }, variant.available ? 10 : 0);
      const best = this.variants
        .filter((variant) => variant.options[index] === changed.value)
        .reduce((a, b) => (!a || score(b) > score(a) ? b : a), null);
      if (!best) return;
      this.selects.forEach((select) => {
        select.value = best.options[Number(select.dataset.ptOptionIndex)];
      });
    }

    update(changed) {
      if (changed && !this.selectedVariant) this.snapTo(changed);
      this.variant = this.selectedVariant;
      const variant = this.variant;

      if (variant) {
        this.input.value = variant.id;
        this.button.dataset.variantId = variant.id;
        if ('addLabel' in this.button.dataset) this.button.dataset.addLabel = variant.price;
      }
      this.button.dataset.available = String(Boolean(variant && variant.available));

      if (changed && variant) this.showImage(variant);

      if (this.card) {
        this.card.querySelectorAll('[data-pt-variant-price]').forEach((el) => {
          if (variant) el.textContent = variant.price;
        });
        this.card.querySelectorAll('[data-pt-variant-compare]').forEach((el) => {
          el.textContent = (variant && variant.compare) || '';
          el.hidden = !(variant && variant.compare);
        });
      }

      this.render();
    }

    // Points the card image at the variant's image, keeping each src/srcset
    // entry's CDN width so the responsive sizes stay as rendered.
    showImage(variant) {
      if (!this.image || !this.images) return;
      const index = variant.image != null ? variant.image : this.images.fallback;
      const entry = index != null && this.images.images[index];
      if (!entry) return;
      const [base, ratio, alt] = entry;
      const resize = (url) => {
        const next = new URL(base, window.location.href);
        const width = new URL(url, window.location.href).searchParams.get('width');
        if (width) next.searchParams.set('width', width);
        else next.searchParams.delete('width');
        return next.href;
      };
      const current = this.image.getAttribute('src');
      if (current && new URL(current, window.location.href).pathname === new URL(base, window.location.href).pathname) return;
      if (current) this.image.setAttribute('src', resize(current));
      const srcset = this.image.getAttribute('srcset');
      if (srcset) {
        this.image.setAttribute(
          'srcset',
          srcset
            .split(',')
            .map((candidate) => {
              const [url, descriptor] = candidate.trim().split(/\s+/);
              return descriptor ? `${resize(url)} ${descriptor}` : resize(url);
            })
            .join(', ')
        );
      }
      const width = Number(this.image.getAttribute('width'));
      if (width && ratio) this.image.setAttribute('height', String(Math.round(width / ratio)));
      if (alt) this.image.alt = alt;
    }

    render() {
      const variant = this.variant;
      const lines = this.cartItems.filter((item) => Number(item.product_id) === this.productId);
      const sameLine = variant && lines.find((item) => Number(item.variant_id) === variant.id);
      const otherLines = lines.filter((item) => !variant || Number(item.variant_id) !== variant.id);
      // Cart rows (cart page, add-to-cart pop-up) show "Added" and lock once
      // the exact variant is in the cart; homepage/Gifts cards just add more.
      const locksWhenAdded = 'addedLabel' in this.button.dataset;

      let label;
      let disabled = false;
      if (!variant) {
        label = 'Unavailable';
        disabled = true;
      } else if (!variant.available) {
        label = this.button.dataset.soldOutLabel || 'Sold out';
        disabled = true;
      } else if (sameLine && locksWhenAdded) {
        label = this.button.dataset.addedLabel;
        disabled = true;
      } else {
        label = this.button.dataset.addLabel || this.button.dataset.ptAddText || 'Add';
      }

      this.button.disabled = disabled;
      this.input.disabled = disabled;
      if (disabled) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
      if (this.label) this.label.textContent = label;

      if (lines.length === 0) {
        this.inCart.hidden = true;
        return;
      }

      const describe = (item) => {
        const name = item.variant_title || item.title;
        return item.quantity > 1 ? `${name} ×${item.quantity}` : name;
      };
      this.inCartText.textContent = `In your bag: ${lines.map(describe).join(', ')}`;
      this.inCart.hidden = false;

      const canSwap = variant && variant.available && otherLines.length > 0;
      this.swapButton.hidden = !canSwap;
      if (canSwap) {
        this.swapButton.textContent = sameLine ? 'Keep only this one' : `Swap for ${variant.title}`;
      }
    }

    async swap() {
      const variant = this.variant;
      if (!variant || this.swapping) return;
      const otherLines = this.cartItems.filter(
        (item) => Number(item.product_id) === this.productId && Number(item.variant_id) !== variant.id
      );
      const alreadyHasSelected = this.cartItems.some((item) => Number(item.variant_id) === variant.id);

      this.swapping = true;
      this.swapButton.disabled = true;
      try {
        const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
        for (const item of otherLines) {
          await fetch(`${changeUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
        }
        if (alreadyHasSelected) {
          // Nothing to add; just let every cart view catch up.
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          const cart = await fetch(`${cartUrl}.js`).then((response) => response.json());
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pt-variant-picker', cartData: cart });
          else ptCart.refresh();
        } else {
          this.cartItems = this.cartItems.filter((item) => !otherLines.includes(item));
          this.render();
          this.form.requestSubmit(this.button);
        }
      } catch (error) {
        console.error(error);
        ptCart.refresh();
      } finally {
        this.swapping = false;
        this.swapButton.disabled = false;
      }
    }
  }

  customElements.define('pt-variant-picker', PtVariantPicker);
})();
